import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertCircle, Plus, Trash2, Save, Globe, MessageSquare, Building2, Loader2, GraduationCap } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { User, AccountFinal } from '@/api/entities';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';
import {
  getEducationalProductContextsForTenant,
  normalizeEducationalResourceRow,
  serializeEducationalResourcesForApi,
  tenantUsesEducationalProductContext,
} from '@/lib/educationalResources';
import {
  getProductDisplayNameForTenant,
  isSysExamProductContextTenant,
  BLACKBOARD_KEYS,
  isBlackboardProductContextTenant,
  UNIT4_KEYS,
  isUnit4ProductContextTenant,
  CONSTRUCTOR_KEYS,
  isConstructorProductContextTenant,
  ACCESS_PEOPLE_KEYS,
  isAccessPeopleProductContextTenant,
  FOURSCIENCE_KEYS,
  isFourScienceProductContextTenant,
  TESTWE_KEYS,
  isTestweProductContextTenant,
} from '@/lib/accountTargetProducts';
import { useTranslation } from '@/components/utils/translations';

const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'https://api.neogtm.com'
  : 'https://api.neogtm.com';

/** Content/generation languages for Neo (distinct from Settings → Language ui_language codes). */
const CONTENT_LANGUAGES = [
  'English',
  'Spanish',
  'French',
  'German',
  'Brazilian Portuguese',
  'Arabic',
];
const CONTENT_LANGUAGE_SET = new Set(CONTENT_LANGUAGES);
const CONTENT_LANGUAGE_LABEL_KEYS = {
  English: 'english',
  Spanish: 'spanish',
  French: 'french',
  German: 'german',
  'Brazilian Portuguese': 'brazilianPortuguese',
  Arabic: 'arabic',
};
const CONTENT_LANGUAGE_ALIASES = {
  en: 'English',
  'en-US': 'English',
  'en-GB': 'English',
  'English (US)': 'English',
  es: 'Spanish',
  fr: 'French',
  de: 'German',
  ar: 'Arabic',
  pt: 'Brazilian Portuguese',
  'pt-BR': 'Brazilian Portuguese',
  Portuguese: 'Brazilian Portuguese',
  'Portuguese (Brazilian)': 'Brazilian Portuguese',
};

/** Normalize API/legacy values so Radix Select always has a matching SelectItem. */
function normalizeContentLanguage(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return 'English';
  if (CONTENT_LANGUAGE_SET.has(s)) return s;
  if (CONTENT_LANGUAGE_ALIASES[s]) return CONTENT_LANGUAGE_ALIASES[s];
  const hit = CONTENT_LANGUAGES.find((l) => l.toLowerCase() === s.toLowerCase());
  return hit || 'English';
}

function isValidHttpUrl(value) {
  const s = String(value ?? '').trim();
  if (!s) return false;
  try {
    const u = new URL(s);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

function validateEducationalResources(rows, t) {
  const errors = {};
  (rows || []).forEach((row, index) => {
    const title = String(row.title || '').trim();
    const url = String(row.url || '').trim();
    if (!title && !url) return;
    const e = {};
    if (!title) e.title = t('missingRequiredFields');
    else if (title.length > 200) e.title = t('educationalResourceTitleTooLong');
    if (!url) e.url = t('missingRequiredFields');
    else if (!isValidHttpUrl(url)) e.url = t('invalidHttpUrl');
    if (Object.keys(e).length) errors[index] = e;
  });
  return errors;
}

async function fetchEducationalResourcesFromUserApi(userId, tenantId) {
  try {
    const res = await fetch(`${API_BASE_URL}/api/settings/user`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'x-user-id': String(userId),
        'x-tenant-id': String(tenantId),
      },
    });
    if (!res.ok) return [];
    const data = await res.json();
    const raw = data.educational_resources ?? data.settings?.educational_resources;
    if (!Array.isArray(raw)) return [];
    return raw.map((row) => normalizeEducationalResourceRow(row, tenantId));
  } catch (e) {
    console.warn('Educational resources: user settings GET failed', e);
    return [];
  }
}

function normalizeUserUniversities(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((u) => ({ name: u?.name || '' }));
}

function serializeUserUniversitiesForApi(rows) {
  return normalizeUserUniversities(rows)
    .map((u) => ({ name: String(u.name || '').trim() }))
    .filter((u) => u.name);
}

function debounce(fn, ms) {
  let timeoutId;
  const debounced = (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), ms);
  };
  debounced.cancel = () => clearTimeout(timeoutId);
  return debounced;
}

async function putEducationalResourcesUserApi(userId, tenantId, resources) {
  const res = await fetch(`${API_BASE_URL}/api/settings/user`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'x-user-id': String(userId),
      'x-tenant-id': String(tenantId),
    },
    body: JSON.stringify({ educational_resources: resources }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `Save failed (${res.status})`);
  }
}

export default function SettingsPersonalization({ currentTenant: currentTenantProp }) {
  const { t } = useTranslation();
  const contextUser = useUser();
  const contextTenant = useCurrentTenant();
  const currentTenant = currentTenantProp || contextTenant;
  const [currentUser, setCurrentUser] = useState(contextUser);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [apiAvailable, setApiAvailable] = useState(true);

  const resolveTenant = () => {
    const adminActive = contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null;
    return resolveEffectiveTenant(adminActive, currentTenant, contextUser, 'instructure');
  };

  const [effectiveTenant, setEffectiveTenant] = useState(currentTenant || 'instructure');
  const [tenantProductKeys, setTenantProductKeys] = useState([]);

  const [settings, setSettings] = useState({
    language: 'English',
    brandVoice: '',
    stylePrefsOverride: '',
    companyName: '',
    productName: '',
    valueProposition: '',
    products: [], // [{ product_key, product_name, value_proposition }] for multi-product tenants
    kpis: [],
    messagingHooks: [],
    caseStudies: [],
    productLinks: [],
    educationalResources: [],
    userUniversities: [],
  });
  const [educationalResourceErrors, setEducationalResourceErrors] = useState({});
  const [matchCounts, setMatchCounts] = useState({});
  const [totalMatches, setTotalMatches] = useState(0);

  const refreshMatchCounts = useMemo(
    () =>
      debounce(async (universities) => {
        const cleaned = serializeUserUniversitiesForApi(universities);
        if (cleaned.length === 0) {
          setMatchCounts({});
          setTotalMatches(0);
          return;
        }
        const userId = contextUser?.id || contextUser?.user_id;
        if (!effectiveTenant || !userId) return;
        try {
          const res = await fetch(`${API_BASE_URL}/api/settings/alma-mater-match-counts`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'X-Tenant-ID': effectiveTenant,
              'X-User-ID': String(userId),
            },
            body: JSON.stringify({ universities: cleaned }),
          });
          if (!res.ok) return;
          const data = await res.json();
          setMatchCounts(data.counts || {});
          setTotalMatches(data.total || 0);
        } catch {
          // Decorative — silent on failure
        }
      }, 400),
    [effectiveTenant, contextUser]
  );

  useEffect(() => {
    refreshMatchCounts(settings.userUniversities);
    return () => refreshMatchCounts.cancel();
  }, [settings.userUniversities, refreshMatchCounts]);

  useEffect(() => {
    const newTenant = resolveTenant();
    if (newTenant !== effectiveTenant) {
      setEffectiveTenant(newTenant);
    }
  }, [currentTenant, contextUser]);

  useEffect(() => {
    loadSettings();
  }, [effectiveTenant]);

  useEffect(() => {
    loadTenantProductKeys();
  }, [effectiveTenant]);

  // Sync products array when tenantProductKeys changes (multi-product tenants)
  // Only include products valid for this tenant (e.g. SUPPORT/FEEDBACK/TUTOR for learnwise, SYS/EXAM for evasys only)
  useEffect(() => {
    if (tenantProductKeys.length <= 1) return;

    const validKeysSet = new Set(tenantProductKeys);
    setSettings(prev => {
      const existingProducts = Array.isArray(prev.products) ? prev.products : [];
      const existingByKey = new Map(
        existingProducts
          .filter(p => validKeysSet.has(p?.product_key))
          .map(p => [p.product_key, p])
      );

      const merged = tenantProductKeys.map((pk, index) => {
        const existing = existingByKey.get(pk);
        if (existing) return existing;

        return {
          product_key: pk,
          product_name: getProductDisplayNameForTenant(
            effectiveTenant,
            pk,
            existing?.product_name || (index === 0 ? prev.productName : ''),
          ),
          value_proposition:
            index === 0 ? (prev.valueProposition || '') : (existing?.value_proposition || ''),
        };
      });

      return { ...prev, products: merged };
    });
  }, [tenantProductKeys.join(',')]);

  const getTenantProductKeys = async (tenant = effectiveTenant) => {
    try {
      const evasysProducts = ['SYS', 'EXAM'];
      const learnwiseProducts = ['SUPPORT', 'FEEDBACK', 'TUTOR'];
      const cayuseProducts = ['GRAD', 'COMPLIANCE', 'SPONSOR'];

      // Learnwise: always exactly 3 products (SUPPORT, FEEDBACK, TUTOR) — each with own Product Name & Core Value Proposition
      if (tenant === 'learnwise') {
        return learnwiseProducts;
      }

      // Cayuse: GRAD (Haplo), COMPLIANCE (Compliance Suite), SPONSOR (Sponsored Projects)
      if (tenant === 'cayuse') {
        return cayuseProducts;
      }

      // Evasys / Scantron: SYS and EXAM — never show these for any other tenant
      if (isSysExamProductContextTenant(tenant)) {
        const keys = new Set(evasysProducts);
        const accounts = await AccountFinal.filter({ tenant_id: tenant }, '', 500);
        accounts.forEach(acc => {
          const raw = acc.target_products;
          let arr = Array.isArray(raw) ? raw : (typeof raw === 'string' && raw ? [raw] : []);
          if (arr.length === 1 && String(arr[0]).toUpperCase() === 'ALL') {
            arr = evasysProducts;
          }
          arr.forEach(p => {
            const pk = String(p).trim().toUpperCase();
            if (pk && evasysProducts.includes(pk)) keys.add(pk);
          });
        });
        const list = Array.from(keys).filter(Boolean).sort();
        return list.length > 1 ? list : [];
      }

      /* [2026-10-13] THE THREE LENS-CATEGORY TENANTS WERE FALLING THROUGH TO [].
       * blackboard, unit4 and constructor_tech each have their contexts in
       * api/src/config/productContexts.ts AND in admin_atoms.products, but this function
       * only knew learnwise / cayuse / evasys — so tenantProductKeys stayed empty, the
       * `length > 1` gate below never opened, and the per-product Product Name / Core
       * Value Proposition fields simply did not render. The data was there the whole
       * time; nothing switched it on.
       * Keys come from the shared constants rather than being retyped, so these stay
       * identical to the account Product column and to the BE's validation list. */
      if (isBlackboardProductContextTenant(tenant)) return [...BLACKBOARD_KEYS];
      if (isUnit4ProductContextTenant(tenant)) return [...UNIT4_KEYS];
      // [2026-10-15] access_people — same fall-through this comment describes: its contexts
      // are in productContexts.ts and in admin_atoms.products, and without this line
      // tenantProductKeys stayed empty so the per-product Product Name and Core Value
      // Proposition fields never rendered.
      if (isAccessPeopleProductContextTenant(tenant)) return [...ACCESS_PEOPLE_KEYS];
      if (isConstructorProductContextTenant(tenant)) return [...CONSTRUCTOR_KEYS];
      // [2026-10-18] 4science — the SAME fall-through again, which is now three tenants in a row
      // that have shipped product contexts and admin_atoms.products while this list stayed empty,
      // so the per-product fields silently did not render. If a tenant is added to
      // config/productContexts.ts, it belongs here in the same change.
      if (isFourScienceProductContextTenant(tenant)) return [...FOURSCIENCE_KEYS];
      // [2026-10-18] testwe — added in the SAME change as productContexts.ts, per the note above.
      if (isTestweProductContextTenant(tenant)) return [...TESTWE_KEYS];

      return [];
    } catch (e) {
      console.warn('Could not load tenant product keys:', e);
      return [];
    }
  };

  const loadTenantProductKeys = async () => {
    const list = await getTenantProductKeys();
    setTenantProductKeys(list);
  };

  const loadSettings = async () => {
    if (!contextUser) return;
    setIsLoading(true);
    try {
      setCurrentUser(contextUser);

      const resolvedTenantId = resolveEffectiveTenant(
        contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
        currentTenant,
        contextUser,
        'instructure'
      );
      setEffectiveTenant(resolvedTenantId);

      // Resolve tenant product keys first so we have them when processing (avoids stale closure / 2x refresh)
      const resolvedProductKeys = await getTenantProductKeys(resolvedTenantId);

      // Try to load from API
      try {
        const userId = contextUser.id || contextUser.user_id;
        if (!userId) {
          throw new Error('User ID not found');
        }

        const response = await fetch(
          `${API_BASE_URL}/api/settings/personalization?tenant_id=${resolvedTenantId}&user_id=${userId}`,
          {
            credentials: 'include',
            headers: {
              'X-Tenant-ID': resolvedTenantId,
              'X-User-ID': String(userId),
            },
          }
        );

        if (!response.ok) {
          if (response.status === 404) {
            setApiAvailable(false);
            throw new Error('API not available');
          }
          throw new Error('Failed to load settings');
        }

        const data = await response.json();

        const loadedProducts = Array.isArray(data.products) ? data.products : [];
        const isMultiProductTenant = resolvedProductKeys.length > 1;
        const validKeysSet = new Set(resolvedProductKeys);

        // Only use products whose product_key is valid for this tenant (never show SYS/EXAM for learnwise, etc.)
        const filteredProducts = loadedProducts.filter(p => validKeysSet.has(p?.product_key));
        let effectiveProducts = filteredProducts;

        if (isMultiProductTenant) {
          if (filteredProducts.length === 0) {
            effectiveProducts = resolvedProductKeys.map((pk, index) => ({
              product_key: pk,
              product_name: getProductDisplayNameForTenant(
                resolvedTenantId,
                pk,
                index === 0 ? (data.product_name_override || '') : '',
              ),
              value_proposition:
                index === 0 ? (data.value_proposition_override || '') : '',
            }));
          } else {
            // Ensure all tenant product keys have an entry; fill missing from defaults
            const byKey = new Map(filteredProducts.map(p => [p.product_key, p]));
            effectiveProducts = resolvedProductKeys.map((pk, index) => {
              const existing = byKey.get(pk);
              if (existing) return existing;
              return {
                product_key: pk,
                product_name: getProductDisplayNameForTenant(
                  resolvedTenantId,
                  pk,
                  existing?.product_name || (index === 0 ? (data.product_name_override || '') : ''),
                ),
                value_proposition:
                  existing?.value_proposition ||
                  (index === 0 ? (data.value_proposition_override || '') : ''),
              };
            });
          }
        }

        const educationalResources = Array.isArray(data.educational_resources)
          ? data.educational_resources.map((row) =>
              normalizeEducationalResourceRow(row, resolvedTenantId),
            )
          : await fetchEducationalResourcesFromUserApi(userId, resolvedTenantId);

        setSettings({
          language: normalizeContentLanguage(data.language),
          brandVoice: data.brand_voice || '',
          stylePrefsOverride: data.style_prefs_override || '',
          companyName: data.company_name_override || '',
          productName: data.product_name_override || '',
          valueProposition: data.value_proposition_override || '',
          products: effectiveProducts,
          kpis: data.kpis || [],
          messagingHooks: data.hooks || [],
          caseStudies: data.case_study_links || [],
          productLinks: data.product_links || [],
          educationalResources,
          userUniversities: normalizeUserUniversities(data.user_universities),
        });
        setEducationalResourceErrors({});
        setApiAvailable(true);
      } catch (apiError) {
        console.log('API not available, using fallback storage:', apiError.message);
        setApiAvailable(false);
        
        // Fallback to user profile storage
        const personalization = contextUser.personalization || {};
        const tenantSettings = personalization[resolvedTenantId] || {};

        const fallbackProducts = Array.isArray(tenantSettings.products) ? tenantSettings.products : [];
        const isMultiProductTenant = resolvedProductKeys.length > 1;
        const validKeysSet = new Set(resolvedProductKeys);

        const filteredFallback = fallbackProducts.filter(p => validKeysSet.has(p?.product_key));
        let effectiveFallbackProducts = filteredFallback;

        if (isMultiProductTenant) {
          if (filteredFallback.length === 0) {
            effectiveFallbackProducts = resolvedProductKeys.map((pk, index) => ({
              product_key: pk,
              product_name: getProductDisplayNameForTenant(
                resolvedTenantId,
                pk,
                index === 0 ? (tenantSettings.product_name_override || '') : '',
              ),
              value_proposition:
                index === 0 ? (tenantSettings.value_proposition_override || '') : '',
            }));
          } else {
            const byKey = new Map(filteredFallback.map(p => [p.product_key, p]));
            effectiveFallbackProducts = resolvedProductKeys.map((pk, index) => {
              const existing = byKey.get(pk);
              if (existing) return existing;
              return {
                product_key: pk,
                product_name: getProductDisplayNameForTenant(
                  resolvedTenantId,
                  pk,
                  existing?.product_name || (index === 0 ? (tenantSettings.product_name_override || '') : ''),
                ),
                value_proposition:
                  existing?.value_proposition ||
                  (index === 0 ? (tenantSettings.value_proposition_override || '') : ''),
              };
            });
          }
        }

        const educationalResources = Array.isArray(tenantSettings.educational_resources)
          ? tenantSettings.educational_resources.map((row) =>
              normalizeEducationalResourceRow(row, resolvedTenantId),
            )
          : [];

        setSettings({
          language: normalizeContentLanguage(tenantSettings.language),
          brandVoice: tenantSettings.brand_voice || '',
          stylePrefsOverride: tenantSettings.style_prefs_override || '',
          companyName: tenantSettings.company_name_override || '',
          productName: tenantSettings.product_name_override || '',
          valueProposition: tenantSettings.value_proposition_override || '',
          products: effectiveFallbackProducts,
          kpis: tenantSettings.kpis || [],
          messagingHooks: tenantSettings.messaging_hooks || [],
          caseStudies: tenantSettings.case_studies || [],
          productLinks: tenantSettings.product_links || [],
          educationalResources,
          userUniversities: normalizeUserUniversities(tenantSettings.user_universities),
        });
        setEducationalResourceErrors({});
      }

    } catch (error) {
      console.error('Error loading settings:', error);
    }
    setIsLoading(false);
  };

  const saveSettings = async () => {
    if (!contextUser) return;
    const erVal = validateEducationalResources(settings.educationalResources, t);
    if (Object.keys(erVal).length > 0) {
      setEducationalResourceErrors(erVal);
      alert(t('fixRequiredFieldsBeforeSaving'));
      return;
    }
    setEducationalResourceErrors({});
    setIsSaving(true);
    try {
      const user = contextUser;

      console.log('🔍 DEBUG User object:', {
        user_id: user.user_id,
        id: user.id,
        email: user.email,
        tenant_id: user.tenant_id,
        full_user: user
      });

      let shouldUseFallback = !apiAvailable;

      if (!shouldUseFallback) {
        try {
          const resolvedUserId = user?.id || user?.user_id;
          const resolvedTenantId = resolveEffectiveTenant(
            user?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
            currentTenant,
            user,
            'instructure'
          );

          if (!resolvedUserId || !resolvedTenantId) {
            throw new Error('Missing user or tenant context');
          }

          const isMultiProductTenant = tenantProductKeys.length > 1;
          const firstNonEmptyProductName = (settings.products || [])
            .map(p => (p.product_name || '').trim())
            .find(Boolean) || '';
          const firstNonEmptyValueProp = (settings.products || [])
            .map(p => (p.value_proposition || '').trim())
            .find(Boolean) || '';
          const effectiveProductName = isMultiProductTenant
            ? firstNonEmptyProductName
            : settings.productName;
          const effectiveValueProposition = isMultiProductTenant
            ? firstNonEmptyValueProp
            : settings.valueProposition;

          const payload = {
            tenant_id: resolvedTenantId,
            user_id: resolvedUserId,
            language: settings.language,
            brand_voice: settings.brandVoice,
            style_prefs_override: settings.stylePrefsOverride,
            company_name_override: settings.companyName,
            product_name_override: effectiveProductName,
            value_proposition_override: effectiveValueProposition,
            products: settings.products,
            kpis: settings.kpis,
            hooks: settings.messagingHooks,
            case_study_links: settings.caseStudies,
            product_links: settings.productLinks,
            user_universities: serializeUserUniversitiesForApi(settings.userUniversities),
          };

          console.log('🧪 Save payload preview:', {
            tenant: resolvedTenantId,
            user_id: resolvedUserId,
            isMultiProductTenant,
            productName: settings.productName,
            valueProposition: settings.valueProposition,
            products: settings.products,
            effectiveProductName,
            effectiveValueProposition
          });

          const response = await fetch(`${API_BASE_URL}/api/settings/personalization`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Tenant-ID': resolvedTenantId,
              'X-User-ID': String(resolvedUserId),
            },
            credentials: 'include',
            body: JSON.stringify(payload)
          });

          if (!response.ok) {
            const errorText = await response.text();
            console.error('❌ Personalization API error:', response.status, errorText);
            throw new Error(`API save failed: ${response.status} ${errorText}`);
          }

          await putEducationalResourcesUserApi(
            resolvedUserId,
            resolvedTenantId,
            serializeEducationalResourcesForApi(settings.educationalResources, resolvedTenantId),
          );

          alert(t('settingsSavedSuccessfully'));
          await loadSettings();
          setIsSaving(false);
          return;
        } catch (apiError) {
          console.log('API save failed, using fallback:', apiError.message);
          setApiAvailable(false);
          shouldUseFallback = true;
        }
      }

      if (shouldUseFallback) {
        // Fallback to user profile storage
        const currentPersonalization = user.personalization || {};
        currentPersonalization[effectiveTenant] = {
          language: settings.language,
          brand_voice: settings.brandVoice,
          style_prefs_override: settings.stylePrefsOverride,
          company_name_override: settings.companyName,
          product_name_override: settings.productName,
          value_proposition_override: settings.valueProposition,
          products: settings.products,
          kpis: settings.kpis,
          messaging_hooks: settings.messagingHooks,
          case_studies: settings.caseStudies,
          product_links: settings.productLinks,
          educational_resources: serializeEducationalResourcesForApi(
            settings.educationalResources,
            effectiveTenant,
          ),
          user_universities: serializeUserUniversitiesForApi(settings.userUniversities),
        };

        await User.updateMyUserData({
          personalization: currentPersonalization
        });

        try {
          const resolvedUserId = user?.id || user?.user_id;
          if (resolvedUserId) {
            await putEducationalResourcesUserApi(
              resolvedUserId,
              effectiveTenant,
              serializeEducationalResourcesForApi(settings.educationalResources, effectiveTenant),
            );
          }
        } catch (putErr) {
          console.warn('Educational resources PUT (fallback) failed:', putErr);
        }

        setCurrentUser(prev => prev ? { ...prev, personalization: currentPersonalization } : null);
        alert(t('settingsSavedSuccessfully'));
      }

    } catch (error) {
      console.error('Error saving settings:', error);
      alert(t('failedToSaveSettingsTryAgain'));
    }
    setIsSaving(false);
  };

  const addKPI = () => {
    setSettings(prev => ({
      ...prev,
      kpis: [...prev.kpis, { name: '', description: '' }]
    }));
  };

  const updateKPI = (index, field, value) => {
    setSettings(prev => {
      const newKPIs = [...prev.kpis];
      if (field === 'product_key') {
        newKPIs[index] = { ...newKPIs[index], product_key: value || undefined };
      } else {
        newKPIs[index] = { ...newKPIs[index], [field]: value };
      }
      return { ...prev, kpis: newKPIs };
    });
  };

  const removeKPI = (index) => {
    setSettings(prev => ({
      ...prev,
      kpis: prev.kpis.filter((_, i) => i !== index)
    }));
  };

  const addUniversity = () =>
    setSettings((prev) => ({
      ...prev,
      userUniversities: [...(prev.userUniversities || []), { name: '' }],
    }));

  const updateUniversityName = (index, name) =>
    setSettings((prev) => {
      const rows = [...(prev.userUniversities || [])];
      rows[index] = { name };
      return { ...prev, userUniversities: rows };
    });

  const removeUniversity = (index) =>
    setSettings((prev) => ({
      ...prev,
      userUniversities: (prev.userUniversities || []).filter((_, idx) => idx !== index),
    }));

  const updateProduct = (index, field, value) => {
    setSettings(prev => {
      const newProducts = [...(prev.products || [])];
      if (!newProducts[index]) newProducts[index] = { product_key: '', product_name: '', value_proposition: '' };
      newProducts[index] = { ...newProducts[index], [field]: value };
      return { ...prev, products: newProducts };
    });
  };

  const addMessagingHook = () => {
    setSettings(prev => ({
      ...prev,
      messagingHooks: [...prev.messagingHooks, { hook: '', archetype: 'Universal' }]
    }));
  };

  const updateMessagingHook = (index, field, value) => {
    setSettings(prev => {
      const newHooks = [...prev.messagingHooks];
      
      // Convert string to object if needed
      if (typeof newHooks[index] === 'string') {
        newHooks[index] = {
          hook: newHooks[index],
          archetype: 'Universal'
        };
      }
      
      // Update the field
      if (field === 'hook') {
        newHooks[index].hook = value;
      } else if (field === 'archetype') {
        newHooks[index].archetype = value;
      } else if (field === 'product_key') {
        newHooks[index].product_key = value || undefined;
      }
      
      return { ...prev, messagingHooks: newHooks };
    });
  };

  const removeMessagingHook = (index) => {
    setSettings(prev => ({
      ...prev,
      messagingHooks: prev.messagingHooks.filter((_, i) => i !== index)
    }));
  };

  const addCaseStudy = () => {
    setSettings(prev => ({
      ...prev,
      caseStudies: [...prev.caseStudies, { type: 'case_study', title: '', description: '', url: '' }]
    }));
  };

  const updateCaseStudy = (index, field, value) => {
    setSettings(prev => {
      const newCaseStudies = [...prev.caseStudies];
      
      // If changing type, reset fields appropriately
      if (field === 'type') {
        if (value === 'client_reference') {
          // Client reference only needs title
          newCaseStudies[index] = { 
            type: value, 
            title: newCaseStudies[index].title || '' 
          };
        } else if (value === 'case_study') {
          // Case study needs title, url, description
          newCaseStudies[index] = { 
            type: value, 
            title: newCaseStudies[index].title || '',
            url: '',
            description: ''
          };
        }
      } else if (field === 'product_key') {
        newCaseStudies[index].product_key = value || undefined;
      } else {
        // Normal field update
        newCaseStudies[index] = { ...newCaseStudies[index], [field]: value };
      }
      
      return { ...prev, caseStudies: newCaseStudies };
    });
  };

  const removeCaseStudy = (index) => {
    setSettings(prev => ({
      ...prev,
      caseStudies: prev.caseStudies.filter((_, i) => i !== index)
    }));
  };

  const addProductLink = () => {
    setSettings(prev => ({
      ...prev,
      productLinks: [...prev.productLinks, { name: '', url: '' }]
    }));
  };

  const updateProductLink = (index, field, value) => {
    setSettings(prev => {
      const newLinks = [...prev.productLinks];
      if (field === 'product_key') {
        newLinks[index] = { ...newLinks[index], product_key: value || undefined };
      } else {
        newLinks[index] = { ...newLinks[index], [field]: value };
      }
      return { ...prev, productLinks: newLinks };
    });
  };

  const removeProductLink = (index) => {
    setSettings(prev => ({
      ...prev,
      productLinks: prev.productLinks.filter((_, i) => i !== index)
    }));
  };

  const addEducationalResource = () => {
    setSettings(prev => ({
      ...prev,
      educationalResources: [
        ...(prev.educationalResources || []),
        { title: '', url: '', product_context: tenantUsesEducationalProductContext(effectiveTenant) ? 'ALL' : null }
      ]
    }));
  };

  const updateEducationalResource = (index, field, value) => {
    if (field === 'title' || field === 'url') {
      setEducationalResourceErrors(prev => {
        if (!prev[index]) return prev;
        const nextErr = { ...prev[index] };
        delete nextErr[field];
        const next = { ...prev };
        if (Object.keys(nextErr).length === 0) delete next[index];
        else next[index] = nextErr;
        return next;
      });
    }
    setSettings(prev => {
      const rows = [...(prev.educationalResources || [])];
      const cur = rows[index] || {
        title: '',
        url: '',
        product_context: tenantUsesEducationalProductContext(effectiveTenant) ? 'ALL' : null,
      };
      if (field === 'product_context') {
        rows[index] = { ...cur, product_context: value };
      } else {
        rows[index] = { ...cur, [field]: value };
      }
      return { ...prev, educationalResources: rows };
    });
  };

  const removeEducationalResource = (index) => {
    setEducationalResourceErrors(prev => {
      const next = {};
      Object.keys(prev).forEach(k => {
        const i = Number(k);
        if (Number.isNaN(i)) return;
        if (i < index) next[i] = prev[i];
        else if (i > index) next[i - 1] = prev[i];
      });
      return next;
    });
    setSettings(prev => ({
      ...prev,
      educationalResources: (prev.educationalResources || []).filter((_, i) => i !== index)
    }));
  };

  if (!contextUser) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const educationalProductContextOptions =
    getEducationalProductContextsForTenant(effectiveTenant) || [];
  const showEducationalProductContext = tenantUsesEducationalProductContext(effectiveTenant);

  return (
    <div className="space-y-6">
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Settings for <strong>{effectiveTenant}</strong> tenant. These personalization options will be used by NEO when generating content for this tenant.
          {!apiAvailable && <span className="block mt-1 text-xs text-yellow-700">{t('usingLocalStorageApiUnavailableShort')}</span>}
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            {t('languageBrandVoice')}
          </CardTitle>
          <CardDescription>{t('configureNeoCommunicationTenant')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="language">{t('language')}</Label>
            <Select
              value={
                CONTENT_LANGUAGE_SET.has(settings.language)
                  ? settings.language
                  : normalizeContentLanguage(settings.language)
              }
              onValueChange={(value) => setSettings({ ...settings, language: value })}
            >
              <SelectTrigger id="language">
                <SelectValue placeholder={t('selectLanguage')} />
              </SelectTrigger>
              <SelectContent>
                {CONTENT_LANGUAGES.map((lang) => (
                  <SelectItem key={lang} value={lang}>
                    {t(CONTENT_LANGUAGE_LABEL_KEYS[lang])}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="brandVoice">{t('brandVoiceDescription')}</Label>
            <Textarea
              id="brandVoice"
              value={settings.brandVoice}
              onChange={(e) => setSettings({ ...settings, brandVoice: e.target.value })}
              placeholder={t('brandVoiceDescriptionPlaceholder')}
              rows={3}
            />
          </div>

          <div className="pt-4 border-t mt-4">
            <Label htmlFor="stylePrefsOverride">{t('additionalStylePreferencesOverride')}</Label>
            <Textarea
              id="stylePrefsOverride"
              value={settings.stylePrefsOverride}
              onChange={(e) => setSettings({ ...settings, stylePrefsOverride: e.target.value })}
              placeholder={t('additionalStylePreferencesPlaceholder')}
              rows={4}
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('stylePreferencesOverrideHelp')}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5" />
            Your alma mater
          </CardTitle>
          <CardDescription>
            We&apos;ll flag personas who studied or work at the same places — used in People, persona pages, and ad-hoc messaging. Personal to your user; not shared with the rest of your team.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {(settings.userUniversities || []).map((uni, index) => {
            const trimmed = (uni.name || '').trim();
            const count = trimmed ? matchCounts[trimmed] : undefined;
            return (
            <div key={index} className="flex gap-2 items-center p-3 border rounded-lg">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs text-gray-500">University</Label>
                  {trimmed && count !== undefined && (
                    <span className="text-xs text-[#5C6B7A]">
                      {count} {count === 1 ? 'persona' : 'personas'} match
                    </span>
                  )}
                </div>
                <Input
                  placeholder="e.g. Harvard University"
                  value={uni.name || ''}
                  maxLength={200}
                  onChange={(e) => updateUniversityName(index, e.target.value)}
                />
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeUniversity(index)}
                className="text-red-600"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            );
          })}
          <Button onClick={addUniversity} variant="outline" className="w-full">
            <Plus className="w-4 h-4 mr-2" /> Add university
          </Button>
          {totalMatches > 0 && (
            <p className="text-xs text-[#5C6B7A] text-center">
              {totalMatches} matched {totalMatches === 1 ? 'persona' : 'personas'} across all your
              universities.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            {t('companyProduct')}
          </CardTitle>
          <CardDescription>
            {t('customizeCompanyProductPositioning')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="companyName">{t('companyName')}</Label>
            <Input
              id="companyName"
              value={settings.companyName}
              onChange={(e) => setSettings({ ...settings, companyName: e.target.value })}
              placeholder={t('companyNameExample')}
            />
          </div>

          {tenantProductKeys.length > 1 ? (
            /* Multi-product: one block per product with name + value prop */
            <div className="space-y-4">
              {(settings.products || []).map((prod, index) => (
                <div key={prod.product_key || index} className="p-4 border rounded-lg space-y-3">
                  <div>
                    <div className="text-xs font-medium text-[#5C6B7A] mb-1">{prod.product_key}</div>
                    <Label className="text-xs text-gray-500">{t('productName')}</Label>
                    <Input
                      placeholder={`${t('eg')} ${prod.product_key} ${t('productName').toLowerCase()}`}
                      value={prod.product_name || ''}
                      onChange={(e) => updateProduct(index, 'product_name', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">{t('coreValueProposition')}</Label>
                    <Textarea
                      placeholder={t('coreValuePropositionExample')}
                      value={prod.value_proposition || ''}
                      onChange={(e) => updateProduct(index, 'value_proposition', e.target.value)}
                      rows={3}
                      className="mt-1"
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Single product: legacy layout */
            <>
              <div>
                <Label htmlFor="productName">{t('productName')}</Label>
                <Input
                  id="productName"
                  value={settings.productName}
                  onChange={(e) => setSettings({ ...settings, productName: e.target.value })}
                  placeholder={t('productNameExample')}
                />
              </div>

              <div>
                <Label htmlFor="valueProposition">{t('coreValueProposition')}</Label>
                <Textarea
                  id="valueProposition"
                  value={settings.valueProposition}
                  onChange={(e) => setSettings({ ...settings, valueProposition: e.target.value })}
                  placeholder={t('coreValuePropositionExample')}
                  rows={3}
                />
                <p className="text-xs text-gray-500 mt-1">{t('leaveBlankUseTenantDefaultValueProp')}</p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('keyPerformanceIndicators')}</CardTitle>
          <CardDescription>{t('defineMetricsThatMatter')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings.kpis.map((kpi, index) => (
            <div key={index} className="flex gap-2 items-start p-3 border rounded-lg">
              <div className="flex-1 space-y-2">
                <Input
                  placeholder={t('kpiNamePlaceholder')}
                  value={kpi.name}
                  onChange={(e) => updateKPI(index, 'name', e.target.value)}
                />
                <Input
                  placeholder={t('kpiDescriptionPlaceholder')}
                  value={kpi.description}
                  onChange={(e) => updateKPI(index, 'description', e.target.value)}
                />
                {tenantProductKeys.length > 0 && (
                  <div>
                    <Label className="text-xs text-gray-500">{t('productOptional')}</Label>
                    <Select
                      value={kpi.product_key || '__all__'}
                      onValueChange={(v) => updateKPI(index, 'product_key', v === '__all__' ? undefined : v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('allProducts')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">{t('allProducts')}</SelectItem>
                        {tenantProductKeys.map(pk => (
                          <SelectItem key={pk} value={pk}>{pk}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeKPI(index)}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button onClick={addKPI} variant="outline" className="w-full">
            <Plus className="w-4 h-4 mr-2" />
            {t('addKpi')}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            {t('messagingHooks')}
          </CardTitle>
          <CardDescription>
            {t('messagingHooksDescription')}
            {tenantProductKeys.length > 0 && ` ${t('useProductSelectorForHooks')}`}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings.messagingHooks.map((hook, index) => (
            <div key={index} className="flex gap-2 items-start p-3 border rounded-lg">
              <div className="flex-1 space-y-2">
                <div>
                  <Label className="text-xs text-gray-500">{t('targetPersona')}</Label>
                  <Select
                    value={typeof hook === 'string' ? 'Universal' : (hook.archetype || 'Universal')}
                    onValueChange={(value) => updateMessagingHook(index, 'archetype', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('selectPersona')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Universal">🌐 {t('universalAllPersonas')}</SelectItem>
                      <SelectItem value="Economic Buyer">💰 {t('economicBuyer')}</SelectItem>
                      <SelectItem value="Champion">🎯 {t('champion')}</SelectItem>
                      <SelectItem value="Influencer">🤝 {t('influencer')}</SelectItem>
                      <SelectItem value="Evaluator">🔍 {t('evaluatorTechnical')}</SelectItem>
                      <SelectItem value="End User">🛠️ {t('endUser')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('hookUsedForPersonaType')}
                  </p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">{t('hookText')}</Label>
                  <Input
                    placeholder={t('hookTextExample')}
                    value={typeof hook === 'string' ? hook : hook.hook}
                    onChange={(e) => updateMessagingHook(index, 'hook', e.target.value)}
                  />
                </div>
                {tenantProductKeys.length > 0 && (
                  <div>
                    <Label className="text-xs text-gray-500">{t('productOptional')}</Label>
                    <Select
                      value={typeof hook === 'object' && hook.product_key ? hook.product_key : '__all__'}
                      onValueChange={(v) => updateMessagingHook(index, 'product_key', v === '__all__' ? undefined : v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('allProducts')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">{t('allProducts')}</SelectItem>
                        {tenantProductKeys.map(pk => (
                          <SelectItem key={pk} value={pk}>{pk}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeMessagingHook(index)}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button onClick={addMessagingHook} variant="outline" className="w-full">
            <Plus className="w-4 h-4 mr-2" />
            {t('addMessagingHook')}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('caseStudiesClientReferences')}</CardTitle>
          <CardDescription>
            {t('caseStudiesClientReferencesDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings.caseStudies.map((study, index) => (
            <div key={index} className="flex gap-2 items-start p-3 border rounded-lg">
              <div className="flex-1 space-y-2">
                <div>
                  <Label className="text-xs text-gray-500">{t('type')}</Label>
                  <Select
                    value={study.type || 'case_study'}
                    onValueChange={(value) => updateCaseStudy(index, 'type', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('selectType')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="case_study">📄 {t('caseStudyWithLink')}</SelectItem>
                      <SelectItem value="client_reference">🏫 {t('clientReferenceNameOnly')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {study.type === 'client_reference' ? (
                  <div>
                    <Label className="text-xs text-gray-500">{t('institutionName')}</Label>
                    <Input
                      placeholder={t('institutionNameExample')}
                      value={study.title || ''}
                      onChange={(e) => updateCaseStudy(index, 'title', e.target.value)}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {t('clientReferenceUsagePrefix')} {study.title || '[Name]'} {t('clientReferenceUsageSuffix')}
                    </p>
                  </div>
                ) : (
                  <>
                    <div>
                      <Label className="text-xs text-gray-500">{t('caseStudyTitle')}</Label>
                      <Input
                        placeholder={t('caseStudyTitleExample')}
                        value={study.title || ''}
                        onChange={(e) => updateCaseStudy(index, 'title', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">{t('url')}</Label>
                      <Input
                        placeholder={t('caseStudyUrlPlaceholder')}
                        value={study.url || ''}
                        onChange={(e) => updateCaseStudy(index, 'url', e.target.value)}
                        type="url"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">{t('descriptionOptional')}</Label>
                      <Textarea
                        placeholder={t('briefOutcomeSummary')}
                        value={study.description || ''}
                        onChange={(e) => updateCaseStudy(index, 'description', e.target.value)}
                        rows={2}
                      />
                    </div>
                  </>
                )}
                {tenantProductKeys.length > 0 && (
                  <div>
                    <Label className="text-xs text-gray-500">{t('productOptional')}</Label>
                    <Select
                      value={study.product_key || '__all__'}
                      onValueChange={(v) => updateCaseStudy(index, 'product_key', v === '__all__' ? undefined : v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('allProducts')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">{t('allProducts')}</SelectItem>
                        {tenantProductKeys.map(pk => (
                          <SelectItem key={pk} value={pk}>{pk}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeCaseStudy(index)}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button onClick={addCaseStudy} variant="outline" className="w-full">
            <Plus className="w-4 h-4 mr-2" />
            {t('addCaseStudyClientReference')}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('productLinks')}</CardTitle>
          <CardDescription>{t('usefulResourcesDocumentation')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings.productLinks.map((link, index) => (
            <div key={index} className="flex gap-2 items-start p-3 border rounded-lg">
              <div className="flex-1 space-y-2">
                <Input
                  placeholder={t('linkNamePlaceholder')}
                  value={link.name}
                  onChange={(e) => updateProductLink(index, 'name', e.target.value)}
                />
                <Input
                  placeholder={t('linkUrlPlaceholder')}
                  value={link.url}
                  onChange={(e) => updateProductLink(index, 'url', e.target.value)}
                  type="url"
                />
                {tenantProductKeys.length > 0 && (
                  <div>
                    <Label className="text-xs text-gray-500">{t('productOptional')}</Label>
                    <Select
                      value={link.product_key || '__all__'}
                      onValueChange={(v) => updateProductLink(index, 'product_key', v === '__all__' ? undefined : v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t('allProducts')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">{t('allProducts')}</SelectItem>
                        {tenantProductKeys.map(pk => (
                          <SelectItem key={pk} value={pk}>{pk}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => removeProductLink(index)}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button onClick={addProductLink} variant="outline" className="w-full">
            <Plus className="w-4 h-4 mr-2" />
            {t('addProductLink')}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('educationalResources')}</CardTitle>
          <CardDescription>
            <span className="block">{t('educationalResourcesLead')}</span>
            <span className="block mt-2">{t('educationalResourcesExamples')}</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {(settings.educationalResources || []).map((resource, index) => {
            const err = educationalResourceErrors[index];
            const pcUpper = String(resource.product_context ?? 'ALL').trim().toUpperCase() || 'ALL';
            const selectValue = educationalProductContextOptions.includes(pcUpper)
              ? pcUpper
              : 'ALL';
            return (
              <div key={index} className="flex gap-2 items-start p-3 border rounded-lg">
                <div className="flex-1 space-y-2">
                  <div>
                    <Label className="text-xs text-gray-500">{t('resourceTitle')}</Label>
                    <Input
                      placeholder={t('educationalResourceTitlePlaceholder')}
                      value={resource.title || ''}
                      maxLength={200}
                      onChange={(e) => updateEducationalResource(index, 'title', e.target.value)}
                    />
                    {err?.title ? <p className="text-sm text-red-600 mt-1">{err.title}</p> : null}
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">{t('url')}</Label>
                    <Input
                      placeholder={t('educationalResourceUrlPlaceholder')}
                      value={resource.url || ''}
                      onChange={(e) => updateEducationalResource(index, 'url', e.target.value)}
                      type="url"
                    />
                    {err?.url ? <p className="text-sm text-red-600 mt-1">{err.url}</p> : null}
                  </div>
                  {showEducationalProductContext ? (
                    <div>
                      <Label className="text-xs text-gray-500">{t('productOptional')}</Label>
                      <Select
                        value={selectValue}
                        onValueChange={(v) => updateEducationalResource(index, 'product_context', v)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {educationalProductContextOptions.map((ctx) => (
                            <SelectItem key={ctx} value={ctx}>
                              {ctx === 'ALL' ? t('all') : ctx}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : null}
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeEducationalResource(index)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            );
          })}
          <Button onClick={addEducationalResource} variant="outline" className="w-full">
            <Plus className="w-4 h-4 mr-2" />
            {t('addEducationalResource')}
          </Button>
        </CardContent>
      </Card>

      <Button 
        onClick={saveSettings} 
        disabled={isSaving}
        className="w-full bg-[#00C2C2] hover:bg-[#00A3A3]"
      >
        {isSaving ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {t('saving')}
          </>
        ) : (
          <>
            <Save className="w-4 h-4 mr-2" />
            {t('saveSettings')}
          </>
        )}
      </Button>
    </div>
  );
}